import type { ComponentPropsWithRef, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Inbox } from "lucide-react";
import { cardShellClass } from "@/components/ui/Card";
import { cn } from "@/lib/utils";

export type EmptyStateVariant = "default" | "card" | "dashed" | "dashedWarning";
export type EmptyStateSize = "lg" | "md" | "sm";

export type EmptyStateProps = Omit<ComponentPropsWithRef<"div">, "title" | "children"> & {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  variant?: EmptyStateVariant;
  size?: EmptyStateSize;
  /** When false, omit the icon block (compact message-only layouts). Default true. */
  showIcon?: boolean;
};

const VARIANT_CLASS: Record<EmptyStateVariant, string> = {
  default: "",
  card: cardShellClass,
  dashed: "rounded-xl border border-dashed border-surface-300 bg-surface-50/60",
  dashedWarning:
    "rounded-xl border border-dashed border-warning-200 bg-warning-50/30",
};

const SIZE_CLASS: Record<EmptyStateSize, string> = {
  lg: "px-6 py-16",
  md: "px-4 py-8",
  sm: "px-4 py-6 text-sm",
};

const ICON_WRAP_CLASS: Record<EmptyStateSize, string> = {
  lg: "mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-surface-200 bg-surface-0 shadow-xs",
  md: "mb-3 flex h-10 w-10 items-center justify-center rounded-lg border border-surface-200 bg-surface-0 shadow-xs",
  sm: "mb-2 flex h-8 w-8 items-center justify-center rounded-lg bg-surface-100",
};

const ICON_CLASS: Record<EmptyStateSize, string> = {
  lg: "h-6 w-6 text-surface-500",
  md: "h-5 w-5 text-surface-500",
  sm: "h-4 w-4 text-surface-400",
};

const TITLE_CLASS: Record<EmptyStateSize, string> = {
  lg: "mb-1 text-base font-semibold tracking-tight text-surface-900",
  md: "mb-1 text-sm font-medium text-surface-800",
  sm: "font-normal text-surface-500",
};

const DESCRIPTION_CLASS: Record<EmptyStateSize, string> = {
  lg: "max-w-sm text-sm text-surface-500",
  md: "text-xs text-surface-500",
  sm: "mt-1 text-xs text-surface-400",
};

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  className,
  variant = "default",
  size = "lg",
  showIcon = true,
  ...rest
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        SIZE_CLASS[size],
        VARIANT_CLASS[variant],
        className,
      )}
      {...rest}
    >
      {showIcon ? (
        <div className={ICON_WRAP_CLASS[size]}>
          <Icon className={ICON_CLASS[size]} aria-hidden />
        </div>
      ) : null}
      <h3 className={TITLE_CLASS[size]}>{title}</h3>
      {description ? (
        <p className={DESCRIPTION_CLASS[size]}>{description}</p>
      ) : null}
      {action ? <div className={size === "lg" ? "mt-6" : "mt-4"}>{action}</div> : null}
    </div>
  );
}
